import { customFetchWithRetry } from "./shared";

export class GrainProfileApiError extends Error {
  status?: number | string;
  action?: string;
  details?: string;

  constructor(
    message: string,
    options: { status?: number | string; action?: string; details?: string } = {}
  ) {
    super(message);
    this.name = "GrainProfileApiError";
    this.status = options.status;
    this.action = options.action;
    this.details = options.details;
  }
}

export interface IGrainProfileAPI {
  grainId: string;
  profileId: string;
  profileName: string;
  brokenByShape: boolean;
  brokenThreshold: number;
  particleDistributionRejectKeys?: string[];
  rejectedGrainsRejectKeys?: string[];
  len_calib?: string;
  width_calib?: string;
  rice_type?: string;
  add_calib_r?: number;
  add_calib_g?: number;
  add_calib_b?: number;
  csv_profile?: string;
  rejectionThresholds: Record<string, number>;
  rawJson?: any;
  updatedAt?: number;
  profileType?: string;
  editable?: boolean;
  ownerUsername?: string;
  ownerOrgId?: string;
  createdAt?: number;
  createdBy?: string;
  syncStatus?: string;
  syncError?: string | null;
}

export interface IGrainAPI {
  grainId: string;
  name: string;
  rejectionMapping?: Record<string, string>;
  rawJson?: any;
  updatedAt?: number;
}

const asBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") return value.toLowerCase() === "true" || value === "1";
  return false;
};

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const normalizeRejectionThresholds = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== "object") return {};
  const record = value as Record<string, unknown>;
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(record)) {
    result[key] = asNumber(raw, 0);
  }
  return result;
};

const normalizeProfile = (grainId: string, raw: any): IGrainProfileAPI => {
  const profileId = String(raw?.profileId ?? raw?.profile_id ?? "");
  const legacySelection = raw?.rejectSelectionConfig || raw?.reject_selection_config;
  return {
    grainId: String(raw?.grainId ?? raw?.grain_id ?? grainId),
    profileId,
    profileName: String(raw?.profileName ?? raw?.profile_name ?? ""),
    brokenByShape: asBoolean(raw?.brokenByShape ?? raw?.broken_by_shape),
    brokenThreshold: asNumber(raw?.brokenThreshold ?? raw?.broken_threshold, 0),
    particleDistributionRejectKeys: Array.isArray(raw?.particleDistributionRejectKeys)
      ? raw.particleDistributionRejectKeys.map(String)
      : Array.isArray(raw?.particle_distribution_reject_keys)
        ? raw.particle_distribution_reject_keys.map(String)
        : Array.isArray(legacySelection?.particleDistribution?.selectedKeys)
          ? legacySelection.particleDistribution.selectedKeys.map(String)
        : undefined,
    rejectedGrainsRejectKeys: Array.isArray(raw?.rejectedGrainsRejectKeys)
      ? raw.rejectedGrainsRejectKeys.map(String)
      : Array.isArray(raw?.rejected_grains_reject_keys)
        ? raw.rejected_grains_reject_keys.map(String)
        : Array.isArray(legacySelection?.rejectedGrains?.selectedKeys)
          ? legacySelection.rejectedGrains.selectedKeys.map(String)
          : undefined,
    len_calib: raw?.len_calib ?? raw?.lenCalib,
    width_calib: raw?.width_calib ?? raw?.widthCalib,
    rice_type: raw?.rice_type ?? raw?.riceType,
    add_calib_r: raw?.add_calib_r ?? raw?.addCalibR,
    add_calib_g: raw?.add_calib_g ?? raw?.addCalibG,
    add_calib_b: raw?.add_calib_b ?? raw?.addCalibB,
    csv_profile: raw?.csv_profile ?? raw?.csvProfile,
    rejectionThresholds: normalizeRejectionThresholds(
      raw?.rejectionThresholds ?? raw?.rejection_thresholds
    ),
    rawJson: raw?.rawJson ?? raw,
    updatedAt: raw?.updatedAt ?? raw?.updated_at,
    profileType: raw?.profileType ?? raw?.profile_type,
    editable: raw?.editable,
    ownerUsername: raw?.ownerUsername ?? raw?.owner_username,
    ownerOrgId: raw?.ownerOrgId ?? raw?.owner_org_id,
    createdAt: raw?.createdAt ?? raw?.created_at,
    createdBy: raw?.createdBy ?? raw?.created_by
  };
};

const buildProfilePayload = (
  profileData: Partial<IGrainProfileAPI>,
  profileId?: string
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  if (profileId !== undefined) {
    payload.profileId = profileId;
  }

  if (profileData.profileName !== undefined) {
    payload.profileName = profileData.profileName;
  }

  if (profileData.brokenByShape !== undefined) {
    payload.brokenByShape = profileData.brokenByShape;
  }

  if (profileData.brokenThreshold !== undefined) {
    payload.brokenThreshold = profileData.brokenThreshold;
  }

  if (profileData.len_calib !== undefined) {
    payload.len_calib = profileData.len_calib;
  }

  if (profileData.width_calib !== undefined) {
    payload.width_calib = profileData.width_calib;
  }

  if (profileData.rice_type !== undefined) {
    payload.rice_type = profileData.rice_type;
  }

  if (profileData.add_calib_r !== undefined) {
    payload.add_calib_r = profileData.add_calib_r;
  }

  if (profileData.add_calib_g !== undefined) {
    payload.add_calib_g = profileData.add_calib_g;
  }

  if (profileData.add_calib_b !== undefined) {
    payload.add_calib_b = profileData.add_calib_b;
  }

  if (profileData.csv_profile !== undefined) {
    payload.csv_profile = profileData.csv_profile;
  }

  if (profileData.rejectionThresholds !== undefined) {
    payload.rejectionThresholds = profileData.rejectionThresholds;
  }

  if (profileData.particleDistributionRejectKeys !== undefined) {
    payload.particleDistributionRejectKeys = profileData.particleDistributionRejectKeys;
  }

  if (profileData.rejectedGrainsRejectKeys !== undefined) {
    payload.rejectedGrainsRejectKeys = profileData.rejectedGrainsRejectKeys;
  }

  return payload;
};

const parseErrorResponse = async (response: Response): Promise<string> => {
  try {
    const payload = await response.clone().json();
    if (typeof payload?.error === "string" && payload.error.trim()) {
      return payload.error;
    }
    if (typeof payload?.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload?.detail === "string" && payload.detail.trim()) {
      return payload.detail;
    }
    if (payload && typeof payload === "object") {
      return JSON.stringify(payload);
    }
  } catch {
    // Fall back to text if the response is not JSON.
  }

  try {
    const text = await response.text();
    if (text.trim()) return text;
  } catch {
    // Ignore body parsing failures and fall back to status text.
  }

  return response.statusText || `HTTP ${response.status}`;
};

const ensureOk = async (response: Response, action: string): Promise<void> => {
  if (response.ok) return;
  const details = await parseErrorResponse(response);
  throw new GrainProfileApiError(`${action} failed with ${response.status}: ${details}`, {
    status: response.status,
    action,
    details,
  });
};

const wrapProfileApiError = (prefix: string, error: unknown): Error => {
  const message = error instanceof Error ? error.message : String(error);
  if (error instanceof GrainProfileApiError) {
    return new GrainProfileApiError(`${prefix}: ${message}`, {
      status: error.status,
      action: error.action,
      details: error.details,
    });
  }
  return new Error(`${prefix}: ${message}`);
};

export const isNonRetryableGrainProfileError = (error: unknown): boolean => {
  if (error instanceof GrainProfileApiError) {
    return error.status === 400 || error.status === 403 || error.status === 404;
  }
  return false;
};

export const isGrainProfileNotFoundError = (error: unknown): boolean =>
  error instanceof GrainProfileApiError && error.status === 404;

export const getRejectionMapping = async (grainId: string): Promise<Record<string, string>> => {
  const url = `/grain/${grainId}`;

  try {
    const response = await customFetchWithRetry(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    const grainType = await response.json();

    if (grainType && grainType.rejectionMapping) {
      return grainType.rejectionMapping;
    } else {
      throw new Error(`Rejection mapping not found for grainId ${grainId}`);
    }
  } catch (error) {
    console.error("Error fetching rejection mapping:", (error as Error).message);
    throw new Error(`Failed to fetch rejection mapping: ${(error as Error).message}`);
  }
};

/**
 * Fetch all grain types
 */
export const getAllGrains = async (): Promise<IGrainAPI[]> => {
  const url = `/grain`;

  try {
    const response = await customFetchWithRetry(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    const grains = await response.json();
    return grains;
  } catch (error) {
    console.error("Error fetching grains:", (error as Error).message);
    throw new Error(`Failed to fetch grains: ${(error as Error).message}`);
  }
};

/**
 * Fetch all visible grain profiles for a specific grain (app/org/global filtering is server-side)
 */
export const getGrainProfiles = async (grainId: string): Promise<IGrainProfileAPI[]> => {
  const url = `/grain/${grainId}/profile`;

  try {
    const response = await customFetchWithRetry(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    const profiles = await response.json();
    if (!Array.isArray(profiles)) {
      throw new GrainProfileApiError("Fetching grain profiles failed: expected an array response", {
        action: "Fetching grain profiles",
        details: JSON.stringify(profiles),
      });
    }

    return profiles.map((p) => normalizeProfile(grainId, p));
  } catch (error) {
    console.error("Error fetching grain profiles:", (error as Error).message);
    throw new Error(`Failed to fetch grain profiles: ${(error as Error).message}`);
  }
};

/**
 * Fetch all visible grain profiles across all grains.
 * The backend API is per-grain, so this aggregates results client-side.
 */
export const getAllVisibleGrainProfiles = async (): Promise<IGrainProfileAPI[]> => {
  const grains = await getAllGrains();
  const all = await Promise.all(grains.map((g) => getGrainProfiles(g.grainId)));
  return all.flat();
};

/**
 * Update an existing grain profile
 */
export const updateGrainProfile = async (
  grainId: string,
  profileId: string,
  profileData: Partial<IGrainProfileAPI>
): Promise<IGrainProfileAPI> => {
  const url = `/grain/${grainId}/profile`;

  try {
    const response = await customFetchWithRetry(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify(buildProfilePayload(profileData, profileId)),
    });

    await ensureOk(response, "Updating grain profile");
    const result = await response.json();
    return normalizeProfile(grainId, result);
  } catch (error) {
    console.error("Error updating grain profile:", (error as Error).message);
    throw wrapProfileApiError("Failed to update grain profile", error);
  }
};

/**
 * Create a new grain profile
 */
export const createGrainProfile = async (
  grainId: string,
  profileData: Partial<IGrainProfileAPI>
): Promise<IGrainProfileAPI> => {
  const url = `/grain/${grainId}/profile`;

  try {
    const response = await customFetchWithRetry(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify(buildProfilePayload(profileData, profileData.profileId)),
    });

    await ensureOk(response, "Creating grain profile");
    const result = await response.json();
    return normalizeProfile(grainId, result);
  } catch (error) {
    console.error("Error creating grain profile:", (error as Error).message);
    throw wrapProfileApiError("Failed to create grain profile", error);
  }
};

/**
 * Delete a grain profile
 */
export const deleteGrainProfile = async (
  grainId: string,
  profileId: string
): Promise<{ profileId: string }> => {
  const url = `/grain/${grainId}/profile/${profileId}`;

  try {
    const response = await customFetchWithRetry(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    if (response.status === 404) {
      return { profileId };
    }

    await ensureOk(response, "Deleting grain profile");
    const result = await response.json();
    return { profileId: String(result?.profileId ?? profileId) };
  } catch (error) {
    console.error("Error deleting grain profile:", (error as Error).message);
    throw wrapProfileApiError("Failed to delete grain profile", error);
  }
};
